import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { AuthorizationGroups } from "@/services/partnerApi"
import type { AuthorizationGroup } from "@/services/partnerApi"

const authGroupQueryKeys = {
  all: ["authGroups"] as const,
  detail: (id: string) => [...authGroupQueryKeys.all, "detail", id] as const,
}

export function useAuthGroups(subscriberId?: string, name?: string) {
  return useQuery({
    queryKey: authGroupQueryKeys.all,

    staleTime: 1000 * 60 * 5,

    queryFn: async () => {
      let authGroups: Array<AuthorizationGroup> = []
      const pagination = { offset: 0, limit: 200 }

      while (true) {
        const response = await AuthorizationGroups.ListAuthorizationGroups({
          pagination,
        })
        if (response.authorization_groups.length === 0) {
          break
        }
        authGroups = authGroups.concat(response.authorization_groups)
        if (response.authorization_groups.length < pagination.limit) {
          break
        }
        pagination.offset += response.authorization_groups.length
      }

      return authGroups
    },

    select: (authGroups) =>
      authGroups.filter((ag) => {
        // If subscriberId is provided, only return authGroups that match
        if (subscriberId && ag.subscriber_id !== subscriberId) {
          return false
        }

        // If name is provided, only return authGroups that include the search value in the name or id
        if (name) {
          return (
            ag.name?.toLowerCase().includes(name.toLowerCase()) ||
            ag.id.toLowerCase().includes(name.toLowerCase())
          )
        }

        // If no filters are provided, return all authGroups
        return true
      }),
  })
}

export function useAuthGroup(id: string) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: authGroupQueryKeys.detail(id),

    queryFn: () =>
      AuthorizationGroups.GetAuthorizationGroup({ id }).then(
        (r) => r.authorization_group!,
      ),

    initialData: () =>
      queryClient
        .getQueryData<Array<AuthorizationGroup>>(authGroupQueryKeys.all)
        ?.find((s) => s.id === id),

    initialDataUpdatedAt: () =>
      queryClient.getQueryState(authGroupQueryKeys.all)?.dataUpdatedAt,
  })
}

export function useCreateAuthGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: AuthorizationGroups.CreateAuthorizationGroup,

    onMutate: async (newAuthGroup) => {
      await queryClient.cancelQueries({ queryKey: authGroupQueryKeys.all })
      const previous = queryClient.getQueryData(authGroupQueryKeys.all)
      queryClient.setQueryData<Array<AuthorizationGroup>>(
        authGroupQueryKeys.all,
        (old) => {
          return old?.concat(newAuthGroup)
        },
      )
      return { previous }
    },

    onError: (_err, _newAuthGroup, context) => {
      if (context) {
        queryClient.setQueryData(authGroupQueryKeys.all, context.previous)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: authGroupQueryKeys.all })
    },
  })
}

export function useUpdateAuthGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: AuthorizationGroups.UpdateAuthorizationGroup,

    onMutate: async (newAuthGroup) => {
      await queryClient.cancelQueries({ queryKey: authGroupQueryKeys.all })
      const previous = queryClient.getQueryData(authGroupQueryKeys.all)
      queryClient.setQueryData<Array<AuthorizationGroup>>(
        authGroupQueryKeys.all,
        (old) => {
          return old?.map((ag) =>
            ag.id === newAuthGroup.id ? newAuthGroup : ag,
          )
        },
      )
      return { previous }
    },

    onError: (_err, _newAuthGroup, context) => {
      if (context) {
        queryClient.setQueryData(authGroupQueryKeys.all, context.previous)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: authGroupQueryKeys.all })
    },
  })
}

export function useDeleteAuthGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: AuthorizationGroups.DeleteAuthorizationGroup,

    onMutate: async (authGroup) => {
      await queryClient.cancelQueries({ queryKey: authGroupQueryKeys.all })
      const previous = queryClient.getQueryData(authGroupQueryKeys.all)
      queryClient.setQueryData<Array<AuthorizationGroup>>(
        authGroupQueryKeys.all,
        (old) => {
          return old?.filter((s) => s.id !== authGroup.id)
        },
      )
      return { previous }
    },

    onError: (_err, _authGroup, context) => {
      if (context) {
        queryClient.setQueryData(authGroupQueryKeys.all, context.previous)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: authGroupQueryKeys.all })
    },
  })
}
